/**
 * Theme Performance Optimizer
 * 
 * This script optimizes Dawn theme performance by:
 * 1. Deferring non-critical resources
 * 2. Preloading critical assets
 * 3. Optimizing image loading
 * 4. Implementing responsive content loading
 */

(function() {
  'use strict';
  
  // Configuration
  const config = {
    // Assets that should be preloaded for critical path rendering
    criticalAssets: [
      '/cdn/shop/t/1/assets/base.css',
      '/cdn/shop/t/1/assets/component-price.css'
    ],
    // Scripts that can be deferred
    deferScripts: true,
    // Enable lazy loading for images
    lazyLoadImages: true,
    // Enable responsive content loading
    responsiveLoading: true
  };
  
  /**
   * Initialize the performance optimizations
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', optimize);
    } else {
      optimize();
    }
    
    // Apply immediate optimizations that don't need DOM to be ready
    optimizeHeadResources();
  }
  
  /**
   * Main optimization function - runs when DOM is ready
   */
  function optimize() {
    if (config.lazyLoadImages) {
      enhanceImageLoading();
    }
    
    if (config.responsiveLoading) {
      setupResponsiveLoading();
    }
    
    // Track page performance
    monitorPerformance();
    
    // Fix common theme issues
    applyThemeFixes();
  }
  
  /**
   * Optimize resources in the document head
   */
  function optimizeHeadResources() {
    // Preload critical assets
    config.criticalAssets.forEach(asset => {
      if (!document.querySelector(`link[href="${asset}"]`)) {
        const preload = document.createElement('link');
        preload.href = asset;
        preload.rel = 'preload';
        preload.as = asset.endsWith('.css') ? 'style' : 'script';
        document.head.appendChild(preload);
      }
    });
    
    // Defer non-critical scripts
    if (config.deferScripts) {
      const scripts = document.querySelectorAll('script[src]:not([defer]):not([async])');
      scripts.forEach(script => {
        // Don't modify scripts that are already optimized or required for critical functionality
        if (!script.hasAttribute('data-critical') && 
            !script.src.includes('jquery') && 
            !script.src.includes('lazysizes')) {
          script.defer = true;
        }
      });
    }
  }
  
  /**
   * Enhance image loading for better performance
   */
  function enhanceImageLoading() {
    const images = document.querySelectorAll('img:not([loading])');
    
    images.forEach(img => {
      // Skip small images that are likely icons
      if (img.height < 50 || img.width < 50) return;
      
      // Add lazy loading
      img.loading = 'lazy';
      
      // Add decoding async for better performance
      img.decoding = 'async';
      
      // Make sure images have proper alt text
      if (!img.alt) {
        // For product images, use product title as alt text
        const productTitle = document.querySelector('.product__title h1');
        if (productTitle && img.closest('.product__media-item')) {
          img.alt = productTitle.textContent.trim();
        } else {
          img.alt = ''; // Empty alt for decorative images
        }
      }
    });
    
    // Fix poster images in videos
    const deferredMedia = document.querySelectorAll('deferred-media');
    deferredMedia.forEach(media => {
      const poster = media.querySelector('img');
      if (poster) {
        poster.loading = 'eager'; // Don't lazy load poster images
      }
    });
  }
  
  /**
   * Set up responsive loading for different device types
   */
  function setupResponsiveLoading() {
    // Only load certain elements on desktop
    if (window.innerWidth >= 990) {
      loadDesktopOnlyContent();
    } else {
      loadMobileOnlyContent();
    }
    
    // Update on resize (with debounce)
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (window.innerWidth >= 990) {
          loadDesktopOnlyContent();
        } else {
          loadMobileOnlyContent();
        }
      }, 250);
    });
  }
  
  /**
   * Load desktop-only content
   */
  function loadDesktopOnlyContent() {
    // Load desktop-specific elements
    document.querySelectorAll('[data-desktop-only]').forEach(el => {
      el.style.display = '';
    });
    
    // Hide mobile-only elements
    document.querySelectorAll('[data-mobile-only]').forEach(el => {
      el.style.display = 'none';
    });
  }
  
  /**
   * Load mobile-only content
   */
  function loadMobileOnlyContent() {
    // Load mobile-specific elements
    document.querySelectorAll('[data-mobile-only]').forEach(el => {
      el.style.display = '';
    });
    
    // Hide desktop-only elements
    document.querySelectorAll('[data-desktop-only]').forEach(el => {
      el.style.display = 'none';
    });
  }
  
  /**
   * Monitor performance metrics
   */
  function monitorPerformance() {
    // Only run when Performance API is available
    if (!window.performance || !window.performance.getEntriesByType) {
      return;
    }
    
    // Wait for page to be fully loaded
    window.addEventListener('load', () => {
      setTimeout(() => {
        // Get performance metrics
        const pageNavigation = performance.getEntriesByType('navigation')[0];
        const paintMetrics = performance.getEntriesByType('paint');
        
        if (pageNavigation) {
          // Log key metrics if in development mode
          if (window.Shopify && window.Shopify.designMode) {
            console.info('⚡ Page Load Performance ⚡');
            console.info(`DOM Content Loaded: ${pageNavigation.domContentLoadedEventEnd.toFixed(0)}ms`);
            console.info(`Full Page Load: ${pageNavigation.loadEventEnd.toFixed(0)}ms`);
            
            // Log paint metrics
            const fcp = paintMetrics.find(entry => entry.name === 'first-contentful-paint');
            if (fcp) {
              console.info(`First Contentful Paint: ${fcp.startTime.toFixed(0)}ms`);
            }
          }
          
          // Send metrics to analytics if needed
          if (typeof window.ga === 'function') {
            window.ga('send', 'timing', {
              'timingCategory': 'Page Performance',
              'timingVar': 'load',
              'timingValue': pageNavigation.loadEventEnd
            });
          }
        }
      }, 0);
    });
  }
  
  /**
   * Apply fixes for common theme issues
   */
  function applyThemeFixes() {
    // Fix cart drawer issues
    fixCartDrawer();
    
    // Fix product gallery navigation
    fixProductGallery();
    
    // Fix mobile tap highlight color
    fixMobileTapHighlight();
  }
  
  /**
   * Fix issues with cart drawer
   */
  function fixCartDrawer() {
    // Fix empty cart image display
    const emptyCartImage = document.querySelector('.cart-drawer__empty-content .cart__empty-text svg');
    if (emptyCartImage) {
      emptyCartImage.setAttribute('aria-hidden', 'true');
      emptyCartImage.setAttribute('focusable', 'false');
    }
    
    // Fix cart drawer header in empty state
    const emptyCartDrawer = document.querySelector('cart-drawer.is-empty');
    if (emptyCartDrawer) {
      const header = emptyCartDrawer.querySelector('.drawer__header');
      if (header) {
        header.style.display = 'none';
      }
    }
    
    // Make sure drawer close button is accessible
    document.querySelectorAll('.drawer__close').forEach(closeBtn => {
      if (!closeBtn.getAttribute('aria-label')) {
        closeBtn.setAttribute('aria-label', 'Close drawer');
      }
    });
  }
  
  /**
   * Fix product gallery navigation issues
   */
  function fixProductGallery() {
    // Ensure gallery thumbnails are visible on desktop
    const thumbnailGallery = document.querySelector('[id^="GalleryThumbnails-"]');
    if (thumbnailGallery && window.innerWidth >= 750) {
      thumbnailGallery.style.display = 'block';
      thumbnailGallery.style.opacity = '1';
      thumbnailGallery.style.visibility = 'visible';
    }
    
    // Make sure product zoom icon is accessible
    const productMedia = document.querySelectorAll('.product__media');
    productMedia.forEach(media => {
      // Add zoom indicator if not present
      if (!media.querySelector('.zoom-indicator') && !media.querySelector('iframe') && !media.querySelector('video')) {
        const zoomIndicator = document.createElement('span');
        zoomIndicator.className = 'zoom-indicator';
        zoomIndicator.setAttribute('aria-hidden', 'true');
        media.appendChild(zoomIndicator);
      }
    });
  }
  
  /**
   * Fix mobile tap highlight color
   */
  function fixMobileTapHighlight() {
    // Create a style element if needed
    if (!document.getElementById('tap-highlight-fix')) {
      const style = document.createElement('style');
      style.id = 'tap-highlight-fix';
      style.textContent = `
        * {
          -webkit-tap-highlight-color: rgba(238, 86, 158, 0.3) !important;
        }
        
        /* Prevent highlight on product images */
        .product__media img, .product-media-container img {
          -webkit-tap-highlight-color: transparent !important;
        }
      `;
      document.head.appendChild(style);
    }
  }
  
  // Start the optimization process
  init();
})();